import React, { useEffect } from 'react'
import { useRouteMatch } from 'react-router-dom'

import {
  Box,
  Card,
  CircularProgress,
  ProviderLogo,
  RoundedPlainTextButton,
  Typography,
  makeAppStyles,
} from '@percept/mui'

import { CheckCircleOutline, ErrorOutline, InfoOutlined, Lock, OpenInNew } from '@percept/mui/icons'

import { usePublicOAuthInvitation } from '@percept/hooks'

import { getBrandingPlatformName, providerLabelMap } from '@percept/constants'

import { format as dateFormat, parseISO } from 'date-fns'

import { AppBranding, OAuthInvitationStatus } from '@percept/types'


export type OAuthInvitationLandingProps = {
  /* Optional callback fired with the invitation's branding once it has been
   * resolved. Multi-tenant apps (e.g. dashboard) wire this to their theme
   * switcher to render the landing page in the same brand the recipient saw
   * in the invitation email. Single-tenant apps (e.g. vodafone) omit it. */
  onBrandingResolved?: (branding: AppBranding) => void
}


const useStyles = makeAppStyles( theme => ({
  card: {
    width: '100%',
    maxWidth: 520,
  },
  cardBody: {
    padding: theme.spacing(5),
  },
  providerRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  providerLabel: {
    marginLeft: theme.spacing(1.5),
    fontWeight: theme.typography.fontWeightMedium,
  },
  primaryAction: {
    marginTop: theme.spacing(4),
  },
  statusIcon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(2),
  },
  successIcon: {
    color: theme.palette.success && theme.palette.success.main || theme.palette.primary.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  infoBox: {
    color: theme.palette.info.main,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 13,
    marginTop: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    marginRight: theme.spacing(0.75),
  },
}))


const terminalCopy: Record<Exclude<OAuthInvitationStatus, 'PENDING'>, {
  title: string
  body: string
  variant: 'success' | 'error'
}> = {
  COMPLETED: {
    title: 'Authorisation Complete',
    body: 'This invitation has already been used. You can safely close this page.',
    variant: 'success',
  },
  EXPIRED: {
    title: 'This invitation has expired',
    body: 'Please contact your Percept representative for a new invitation.',
    variant: 'error',
  },
  REVOKED: {
    title: 'This invitation has been revoked',
    body: 'Please contact your Percept representative if this is unexpected.',
    variant: 'error',
  },
  FAILED: {
    title: 'Authorisation Failed',
    body: 'The authorisation could not be completed. Please contact your Percept representative for a new invitation.',
    variant: 'error',
  },
}


export const OAuthInvitationLanding = ({
  onBrandingResolved,
}: OAuthInvitationLandingProps): JSX.Element => {

  const classes = useStyles()

  const match = useRouteMatch<{ token: string }>()
  const token = match.params.token || null

  const invitation = usePublicOAuthInvitation(token)

  useEffect(() => {
    if( invitation.data && onBrandingResolved ){
      onBrandingResolved(invitation.data.branding)
    }
  }, [invitation.data, onBrandingResolved])

  if( invitation.isLoading ){
    return (
      <Card className={classes.card}>
        <Box textAlign='center' className={classes.cardBody}>
          <CircularProgress className={classes.statusIcon} size='3rem' />
          <Typography variant='h6' color='textPrimary'>
            Loading authorisation link...
          </Typography>
        </Box>
      </Card>
    )
  }

  if( invitation.error || !invitation.data ){
    const platformName = getBrandingPlatformName(process.env.BRANDING as AppBranding)
    return (
      <Card className={classes.card}>
        <Box textAlign='center' className={classes.cardBody}>
          <ErrorOutline className={`${classes.statusIcon} ${classes.errorIcon}`} />
          <Typography variant='h5' gutterBottom>
            Invitation Not Found
          </Typography>
          <Typography variant='body1' color='textSecondary'>
            This link could not be located. Please double-check the URL or contact your {platformName} representative.
          </Typography>
        </Box>
      </Card>
    )
  }

  const { provider, org_unit_name, status, expires_at, oauth_url, branding } = invitation.data
  const providerLabel = providerLabelMap[provider]
  const platformName = getBrandingPlatformName(branding || process.env.BRANDING as AppBranding)

  if( status !== 'PENDING' ){
    const copy = terminalCopy[status]
    return (
      <Card className={classes.card}>
        <Box textAlign='center' className={classes.cardBody}>
          { copy.variant === 'success' ? (
            <CheckCircleOutline className={`${classes.statusIcon} ${classes.successIcon}`} />
          ) : (
            <ErrorOutline className={`${classes.statusIcon} ${classes.errorIcon}`} />
          )}
          <Typography variant='h5' gutterBottom>
            {copy.title}
          </Typography>
          <Typography variant='body1' color='textSecondary'>
            {copy.body}
          </Typography>
        </Box>
      </Card>
    )
  }

  if( !oauth_url ){
    return (
      <Card className={classes.card}>
        <Box textAlign='center' className={classes.cardBody}>
          <ErrorOutline className={`${classes.statusIcon} ${classes.errorIcon}`} />
          <Typography variant='h5' gutterBottom>
            Unable to Start Authorisation
          </Typography>
          <Typography variant='body1' color='textSecondary'>
            We could not prepare an authorisation link. Please try again, or contact your {platformName} representative.
          </Typography>
        </Box>
      </Card>
    )
  }

  const expiry = parseISO(expires_at)

  return (
    <Card className={classes.card}>
      <Box className={classes.cardBody}>
        <div className={classes.providerRow}>
          <ProviderLogo provider={provider} size={2} />
        </div>

        <Typography variant='h5' gutterBottom>
          Authorise Access for {org_unit_name}
        </Typography>

        <Typography variant='body1' color='textSecondary' gutterBottom>
          {platformName} has asked you to grant access to <strong>{providerLabel}</strong> on
          behalf of <strong>{org_unit_name}</strong>.
        </Typography>

        <div className={classes.infoBox}>
          <InfoOutlined className={classes.infoIcon} color='inherit' />
          <Typography variant='inherit' color='inherit'>
            This link is single-use and expires on {dateFormat(expiry, 'd MMMM yyyy')} at {dateFormat(expiry, 'HH:mm')}
          </Typography>
        </div>

        <Box className={classes.primaryAction} display='flex' justifyContent='center'>
          <RoundedPlainTextButton
            variant='contained'
            color='primary'
            size='large'
            startIcon={<Lock />}
            endIcon={<OpenInNew />}
            onClick={(): void => {
              window.location.assign(oauth_url)
            }}>
            Authorise
          </RoundedPlainTextButton>
        </Box>
      </Box>
    </Card>
  )
}


export default OAuthInvitationLanding
